// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
    Button, 
    SpaceBetween, 
    Box, 
    Container, 
    Header, 
    Link,
    Alert
} from '@cloudscape-design/components';
import { CodeView } from '@cloudscape-design/code-view';
import {
    GLOBAL_TEMPLATE,
    REGIONAL_TEMPLATE,
    useTemplate,
} from '../../Hooks/useTemplate';

import fileDownload from 'js-file-download';
import {GLOBAL_RESOURCES_TEMPLATE_FILENAME} from '../../../config/constants';

const TemplateProvider = () => {
    const {data: globalTemplate, isLoading: isLoadingGlobal} =
        useTemplate(GLOBAL_TEMPLATE);
    const {data: regionalTemplate, isLoading: isLoadingRegional} =
        useTemplate(REGIONAL_TEMPLATE);

    const cdkInstallationCode = `# Method 1: Brazil Config (Recommended)
# Add to your Config file:
dependencies = {
    1.0 = {
        ResourceLensCDKConstructs = 1.0;
    };
};

# Method 2: Internal npm Registry
aws codeartifact login --tool npm --repository amzn --domain amazon --region us-east-1
npm install @amzn/resource-lens-constructs`;

    const cdkUsageCode = `import { ResourceLensGlobalOnboarding, ResourceLensRegionalOnboarding } from '@amzn/resource-lens-constructs';
import { Stack, StackProps } from '@amzn/secure-cdk-sdo';
import { Construct } from 'constructs';

export class MyResourceLensStack extends Stack {
  constructor(scope: Construct, id: string, props?: StackProps) {
    super(scope, id, props);

    // Global resources (deploy once per account)
    new ResourceLensGlobalOnboarding(this, 'ResourceLensGlobal');

    // Regional resources (current region only)
    new ResourceLensRegionalOnboarding(this, 'ResourceLensRegional', {
      region: Stack.of(this).region,
    });
  }
}`;

    return (
        <SpaceBetween direction="vertical" size="l">
            <Box variant="p">
                Choose one of the following methods to onboard your AWS accounts to ResourceLens:
            </Box>

            {/* Option 1: CDK Construct */}
            <Container
                header={
                    <Header
                        variant="h2"
                        description="Recommended approach with zero configuration required"
                    >
                        Option 1: CDK Construct (Recommended)
                    </Header>
                }
            >
                <SpaceBetween direction="vertical" size="m">
                    <Box>
                        <Header variant="h3">Installation</Header>
                        <CodeView content={cdkInstallationCode} />
                    </Box>

                    <Box>
                        <Header variant="h3">Usage Example</Header>
                        <CodeView content={cdkUsageCode} />
                    </Box>

                    <Box>
                        <Header variant="h3">Package Information</Header>
                        <Box variant="p">
                            <strong>Package URL:</strong>{' '}
                            <Link 
                                external 
                                href="https://code.amazon.com/packages/ResourceLensCDKConstructs/trees/mainline"
                            >
                                https://code.amazon.com/packages/ResourceLensCDKConstructs/trees/mainline
                            </Link>
                        </Box>
                        <Box variant="p">
                            <strong>Brazil Package:</strong> ResourceLensCDKConstructs<br/>
                            <strong>npm Package:</strong> @amzn/resource-lens-constructs
                        </Box>
                    </Box>
                </SpaceBetween>
            </Container>

            {/* Option 2: Manual CloudFormation */}
            <Container
                header={
                    <Header
                        variant="h2"
                        description="Alternative method for users who cannot use CDK"
                    >
                        Option 2: Manual CloudFormation Upload
                    </Header>
                }
            >
                <SpaceBetween direction="vertical" size="m">
                    <Box variant="p">
                        Download and deploy the AWS CloudFormation templates below:
                        <ul>
                            <li>
                                Deploy the <strong>Global Resources</strong>{' '}
                                template once in each target account.
                            </li>
                            <li>
                                Deploy the <strong>Regional Resources</strong>{' '}
                                template to each AWS region in a target account that
                                you want to be discoverable.
                            </li>
                        </ul>
                        For a target account to be discovered you must deploy the{' '}
                        <strong>Global Resources</strong> template exactly once, and
                        the <strong>Regional Resources</strong> template at least
                        once.
                    </Box>
                    <Box>
                        <SpaceBetween size="s" direction="horizontal">
                            <Button
                                iconName="download"
                                disabled={isLoadingGlobal}
                                onClick={async () =>
                                    fileDownload(
                                        globalTemplate,
                                        GLOBAL_RESOURCES_TEMPLATE_FILENAME
                                    )
                                }
                            >
                                Global Resources
                            </Button>
                            <Button
                                iconName="download"
                                disabled={isLoadingRegional}
                                onClick={async () =>
                                    fileDownload(
                                        regionalTemplate,
                                        'regional-resources.template'
                                    )
                                }
                            >
                                Regional Resources
                            </Button>
                        </SpaceBetween>
                    </Box>
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
};

export default TemplateProvider;
